import React, { useRef } from 'react';
import classNames from 'classnames';
import Field, { hasDataInFields, validProps } from '../../Field';
import BlockContainer from '../BlockContainer';
import { Link } from '../../Primitives/Link';
import {
  CUSTOM_ID_ANIMATE_BLOCK_IDS,
  HERO_TITLE_REVEAL_SELECTORS_BY_BLOCK,
  PARAGRAPH_REVEAL_BLOCK_IDS,
  SCALE_ANIMATE_BLOCK_IDS,
} from '../../../../config/animatedBlocks';
import { shouldDisableCmsPageAnimations } from '../../../../util/cmsPageAnimations';
import CmsParagraphReveal from '../../../../components/GsapAnimation/CmsParagraphReveal/CmsParagraphReveal';
import css from './BlockDefault.module.css';
import {
  HeroInfoReveal,
  useHeroTitleReveal,
} from '../../../../components/GsapAnimation/HeroTitleReveal/HeroTitleReveal';
import SectionCursor, { useCustomIdAnimate, useScaleAnimate, useScaleReveal } from '../../../../components/GsapAnimation/CustomIdAnimate/CustomIdAnimate';
import { usePseudoElementAnimate, useCenterImgHover } from '../../../../components/GsapAnimation/CustomIdAnimate/MagneticIdAnimation';
import ScrollDown from '../../../../components/CustomBuild/InteractiveCursor/ScrollDown';

// Block IDs that use the custom responsive <picture> media renderer
const VIDEO_CONTAINER_BLOCK_IDS = [
  'video-container',
  'video-container-two',
  'video-container-three',
  'video-container-four',
  'video-container-five',
];

const FieldMedia = props => {
  const { className, media, sizes, options } = props;
  const hasMediaField = hasDataInFields([media], options);
  return hasMediaField ? (
    <div className={classNames(className, css.media)}>
      <Field data={media} sizes={sizes} options={options} />
    </div>
  ) : null;
};

/**
 * Custom responsive media renderer for video-container blocks.
 * Uses a <picture> element with per-breakpoint <source> tags so each
 * device receives the correctly-cropped Cloudinary image without any cropping.
 *
 * Expects media.image.attributes.variants to contain:
 *   mobile  — 9:16 crop (≤600px)
 *   tablet  — 1:1 or 3:4 crop (601px–1024px)
 *   desktop — 16:9 crop (>1024px)
 */
const VideoContainerMedia = props => {
  const { className, media, alt, href } = props;
  const variants = media?.image?.attributes?.variants || {};
  const { mobile, tablet, desktop } = variants;

  if (!desktop?.url) {
    return <div className={classNames(className, css.media, css.videoContainerMedia)} />;
  }

  const picture = (
    <picture>
      {mobile?.url && (
        <source
          media="(max-width: 600px)"
          srcSet={mobile.url}
          width={mobile.width}
          height={mobile.height}
        />
      )}
      {tablet?.url && (
        <source
          media="(max-width: 1024px)"
          srcSet={tablet.url}
          width={tablet.width}
          height={tablet.height}
        />
      )}
      <img
        src={desktop.url}
        width={desktop.width}
        height={desktop.height}
        alt={alt || ''}
        className={css.videoContainerImage}
        loading="eager"
        decoding="async"
      />
    </picture>
  );

  return (
    <div className={classNames(className, css.media, css.videoContainerMedia)}>
      {href ? (
        <Link href={href} className={css.videoContainerLink}>
          {picture}
        </Link>
      ) : (
        picture
      )}
    </div>
  );
};

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This returns a component that can render 'defaultBlock' config.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.blockId id from the block config
 * @param {string} props.blockName name from the block config (not used)
 * @param {'defaultBlock'} props.blockType blockType is set to 'defaultBlock'
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.className add more styles in addition to components own css.root
 * @param {string?} props.mediaClassName add styles for the block's attached media field
 * @param {string?} props.textClassName add styles for the block's attached text field
 * @param {string?} props.ctaButtonClass add styles for the block's attached CTA field
 * @param {Object?} props.title heading config for the block
 * @param {Object?} props.text content config for the block (can be markdown)
 * @param {Object?} props.callToAction call to action button (e.g. internal link config)
 * @param {string?} props.responsiveImageSizes
 * @param {Object} props.options extra options for the block component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents Custom fieldComponents
 * @returns {JSX.Element} component that renders block type: 'defaultBlock'
 */
const BlockDefault = props => {
  const {
    blockId,
    className,
    rootClassName,
    mediaClassName,
    textClassName,
    ctaButtonClass,
    title,
    text,
    callToAction,
    media,
    responsiveImageSizes,
    options,
    alignment,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const hasTextComponentFields = hasDataInFields([title, text, callToAction], options);

  // The block's call-to-action target, so the media can link to the same place
  // as the button rather than being a dead image.
  const ctaProps = validProps(callToAction, options) || {};
  const ctaHref = ctaProps.href;
  const ctaLabel = typeof ctaProps.children === 'string' ? ctaProps.children : null;

  const alignmentClasses = {
    left: css.alignLeft,
    center: css.alignCenter,
    right: css.alignRight,
  };

  const alignmentClass = alignmentClasses[alignment];
  const disableAnimations = shouldDisableCmsPageAnimations(options?.pageId);

  useCustomIdAnimate(
    !disableAnimations && CUSTOM_ID_ANIMATE_BLOCK_IDS.includes(blockId) ? blockId : null,
    {
      fromOpacity: 0,
      fromYPercent: 40,
      triggerStart: 'top 85%',
      triggerEnd: 'top 40%',
      scrub: true,
      once: false,
      duration: 1.4,
      delay: 0,
      markers: false,
    }
  );

  // Magnetic attraction on the two decorative ring spans that proxy
  // #centerimg ::before and ::after — rings pull toward the cursor
  // when it enters their proximity zone, snap back elastically on leave.
  usePseudoElementAnimate(
    !disableAnimations && blockId === 'centerimg' ? 'centerimg' : null,
    {
      strength: .6,
      attractRadius: 320,
      moveDuration: 1.2,
      returnDuration: .92,
      enterTrigger: '0 70%',
    }
  );

  // Scale 0 → 1 scroll reveal — only for #centerimg
  useScaleAnimate(
    !disableAnimations && SCALE_ANIMATE_BLOCK_IDS.includes(blockId) ? blockId : null,
    {
      duration: 1.8,
      delay: 0,
      ease: 'power1.out',
      once: true,
      triggerStart: 'top 80%',
      markers: false,
    }
  );

  // CTA button scale+fade reveal for #centerimg — fires after the block entrance.
  // Uses a ref so it avoids the layout-metric issue that breaks HeroInfoReveal
  // when the parent element starts at scale: 0.6.
  const ctaRef = useRef(null);
  useScaleReveal(
    !disableAnimations && blockId === 'centerimg' ? ctaRef : { current: null },
    {
      duration: 1.6,
      delay: 0.1,
      ease: 'back.out(1.2)',
      triggerStart: 'top 90%',
      once: false,
      scrub: false
    }
  );

  const titleRevealSelectors = HERO_TITLE_REVEAL_SELECTORS_BY_BLOCK[blockId];
  useHeroTitleReveal(
    !disableAnimations && titleRevealSelectors
      ? titleRevealSelectors.map(selector => `#${blockId} ${selector}`).join(', ')
      : null,
    {
      delay: 0.2,
      duration: 0.8,
      fromYPercent: 30,
      staggerAmount: 0.3,
      triggerStart: 'top 100%',
      markers: false,
    }
  );

  return (
    <BlockContainer id={blockId} className={classes}>
      {VIDEO_CONTAINER_BLOCK_IDS.includes(blockId) ? (
        <VideoContainerMedia
          media={media}
          className={mediaClassName}
          alt={title?.content || ''}
          href={ctaHref}
        />
      ) : (
        <FieldMedia
          media={media}
          sizes={responsiveImageSizes}
          className={mediaClassName}
          options={options}
        />
      )}

      {hasTextComponentFields ? (
        <div className={classNames(textClassName, alignmentClass, css.text)}>
          {disableAnimations ? (
            <Field data={title} options={options} />
          ) : (
            <HeroInfoReveal
              staggerAmount={0.4}
              duration={1.8}
              fromYPercent={30}
              triggerStart="top 100%"
              delay={0.2}
              markers={false}
            >
              <Field data={title} options={options} />
            </HeroInfoReveal>
          )}

          {disableAnimations ? (
            <Field data={text} options={options} />
          ) : PARAGRAPH_REVEAL_BLOCK_IDS.includes(blockId) ? (
            <CmsParagraphReveal
              blockId={blockId}
              blockIds={PARAGRAPH_REVEAL_BLOCK_IDS}
              marker={false}
              once={false}
              staggerEach={0.2}
              wordDuration={12}
            >
              <Field data={text} options={options} />
            </CmsParagraphReveal>
          ) : (
            <HeroInfoReveal delay={0.3} duration={1.4} fromYPercent={50} staggerAmount={1.2}>
              <Field data={text} options={options} />
            </HeroInfoReveal>
          )}

          {disableAnimations ? (
            <Field data={callToAction} className={ctaButtonClass} options={options} />
          ) : blockId === 'centerimg' ? (
            <div ref={ctaRef}>
              <Field data={callToAction} className={ctaButtonClass} options={options} />
            </div>
          ) : (
            <HeroInfoReveal delay={0.3} duration={1.4} fromYPercent={50} staggerAmount={1.2}>
              <Field data={callToAction} className={ctaButtonClass} options={options} />
            </HeroInfoReveal>
          )}
        </div>
      ) : null}

      {!disableAnimations && blockId === 'centerimg' ? (
        <SectionCursor sectionId="centerimg" label="Explore" />
      ) : null}
      {VIDEO_CONTAINER_BLOCK_IDS.includes(blockId) ? (
        <ScrollDown sectionId={blockId} href={ctaHref} linkLabel={ctaLabel} />
      ) : null}
    </BlockContainer>
  );
};

export default BlockDefault;
